import { Form, useFormikContext } from "formik"
import { Field, ValueType } from "../../models/workshop/common"
import CheckBoxValidatableInput from "../CheckBoxValidatableInput/CheckBoxValidatableInput"
import styles from "./ApplicationForm.module.scss"
import LoadingButton from "../LoadingButton/LoadingButton"
import { formTranslation } from "../../locales/form"
import { useTranslation } from "react-i18next"
import ApplicationFormHeader from "./ApplicationFormHeader"
import ValidatableInput from "../ValidatableInput/ValidatableInput"
import { SlotDto } from "../../models/slot"
import Button from "../Button/Button"
import { selectCurrentApplicationTemplateVersion } from "../../store/workshop/selectors"
import { useAppSelector } from "../../store/hooks"
import CodeBlockValidatableInput from "../CodeBlockValidatableInput/CodeBlockValidatableInput"
import FormControlWithAdd from "../FormHelpers/ControlWithAdd/FormControlWithAdd"
import FormSection from "../FormHelpers/Section/FormSection"
import { FormikValues } from "formik/dist/types"
import { selectAgents } from "../../store/agents/selectors"

interface ApplicationFormProps {
    fields: Field[]
    title?: string
    icon?: string
    isUpdateAvailable?: boolean
    handleUpdate?: () => void
    isLoading?: boolean
    slotsInApplication?: SlotDto[]
    inScenario?: boolean
    slots: SlotDto[]
}

const tNamespace = "workshop:application."

const ApplicationForm = (props: ApplicationFormProps) => {
    const {
        fields,
        title = "",
        icon = "",
        isUpdateAvailable = false,
        handleUpdate,
        isLoading = false,
        slotsInApplication = [],
        inScenario = false,
        slots
    } = props

    const { t } = useTranslation()
    const agents = useAppSelector(selectAgents)

    const templateCurrentVersion = useAppSelector(selectCurrentApplicationTemplateVersion)

    const isSlotCreated = slots.find(slot => slot.ExternalId.endsWith(templateCurrentVersion?.TemplateId || ""))

    const { values } = useFormikContext<FormikValues>()

    const renderCommonApplicationField = (
        field: Field,
        fieldType?: string, // fill if render as array element type
        name?: string,
        index?: number,
        onDelete?: () => void
    ): JSX.Element => {
        const isArrayElement = name && index !== undefined
        const id = isArrayElement ? `${name}[${index}]` : field.Id
        const label = isArrayElement ? t(`${tNamespace}array-element`) + ` ${index + 1}` : field.Title

        switch (fieldType ?? field.TypeValue.Case) {
            case ValueType.STRING:
                // Title disabled
                const isDisabled = inScenario && field.Id === "title"
                return (
                    <ValidatableInput
                        key={id}
                        id={id}
                        name={id}
                        label={label}
                        onDelete={onDelete}
                        disabled={isDisabled}
                    />
                )
            case ValueType.NUMBER:
                return <ValidatableInput type="number" key={id} id={id} name={id} label={label} onDelete={onDelete} />
            case ValueType.PASSWORD:
                return <ValidatableInput type="password" key={id} id={id} name={id} label={label} onDelete={onDelete} />
            case ValueType.TEXT:
                return <ValidatableInput as="textarea" key={id} id={id} name={id} label={label} onDelete={onDelete} />
            case ValueType.BOOLEAN:
                return (
                    <CheckBoxValidatableInput
                        className={styles.form__checkBox}
                        key={id}
                        id={id}
                        name={id}
                        label={label}
                    />
                )
            case ValueType.SLOT:
                return (
                    <ValidatableInput as="select" id={id} type="text" name={id} label={label} onDelete={onDelete}>
                        {!isSlotCreated &&
                            slotsInApplication.map(slot => (
                                <option value={slot.ExternalId} key={slot.ExternalId}>
                                    {slot.Title}
                                </option>
                            ))}
                        {slots.map(slot => (
                            <option value={slot.ExternalId} key={slot.ExternalId}>
                                {slot.Title}
                            </option>
                        ))}
                    </ValidatableInput>
                )
            case ValueType.AGENT:
                return (
                    <ValidatableInput as="select" id={id} type="text" name={id} label={label} onDelete={onDelete}>
                        <option value="" key="">
                            {t(formTranslation.nothingSelected)}
                        </option>
                        {agents?.map(agent => (
                            <option value={agent.Id} key={agent.Id}>
                                {agent.Id}
                            </option>
                        ))}
                    </ValidatableInput>
                )
            case ValueType.OBJECT:
                return (
                    <CodeBlockValidatableInput id={id} label={label} name={id} language={"json"} onDelete={onDelete} />
                )
            default:
                return <div></div>
        }
    }

    const renderFormByFields = (fields: Field[]): JSX.Element[] =>
        fields.map(field => {
            switch (field.TypeValue.Case) {
                case ValueType.ARRAY:
                    const fieldValue = (values[field.Id] ?? []) as string[]
                    return (
                        <FormControlWithAdd name={field.Id} label={field.Title} onCreateEmptyValue={() => ""} small>
                            {({ name, remove }) =>
                                fieldValue.map((_, index: number) => (
                                    <FormSection key={index}>
                                        {renderCommonApplicationField(field, field.TypeValue.Fields, name, index, () =>
                                            remove(index)
                                        )}
                                    </FormSection>
                                ))
                            }
                        </FormControlWithAdd>
                    )
                default:
                    return renderCommonApplicationField(field)
            }
        })

    return (
        <Form className={styles.form}>
            <ApplicationFormHeader icon={icon} title={title} />
            <div className={styles.form__content}>{renderFormByFields(fields)}</div>
            <div className={styles.form__footer}>
                {isUpdateAvailable && (
                    <Button variant="light" block onClick={handleUpdate}>
                        {t(formTranslation.update)}
                    </Button>
                )}
                <LoadingButton type="submit" variant="primary" block loading={isLoading}>
                    {t(formTranslation.save)}
                </LoadingButton>
            </div>
        </Form>
    )
}

export default ApplicationForm
